import { useState, useRef, useEffect } from 'react'
import { useQuery, useQueries } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import Button from '../components/UI/Button'
import { BarChart3, Users, Building2, Package, Layers, Zap, Shield, ShoppingCart, Mail, MapPin, Globe, Calendar, CreditCard, DollarSign, Download, FileText, FileSpreadsheet, File, Filter, X } from 'lucide-react'
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils'
import toast from 'react-hot-toast'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import { organizationsAPI } from '../services/organizationsAPI'
import productsAPI from '../services/productsAPI'
import api, { paymentsAPI } from '../services/api'

// Export Dropdown Component
function ExportDropdown({ onExportCSV, onExportExcel, onExportPDF }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        title="Export data"
      >
        <Download className="h-4 w-4" />
      </Button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
          <div className="py-1">
            <button
              onClick={() => {
                onExportCSV()
                setIsOpen(false)
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Export as CSV
            </button>
            <button
              onClick={() => {
                onExportExcel()
                setIsOpen(false)
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Export as Excel
            </button>
            <button
              onClick={() => {
                onExportPDF()
                setIsOpen(false)
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <File className="h-4 w-4" />
              Export as PDF
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Reports() {
  // Filter state
  const [selectedPeriod, setSelectedPeriod] = useState('all')
  const [orgStatusFilter, setOrgStatusFilter] = useState('')
  const [productStatusFilter, setProductStatusFilter] = useState('')

  // Helper function to get date cutoff based on period
  const getPeriodCutoff = (period) => {
    if (period === 'all') return null
    
    const now = new Date()
    let cutoff = new Date(now)
    
    switch (period) {
      case 'monthly':
        cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())
        break
      case '6months':
        cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
        break
      case '1year':
        cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
        break
      default:
        return null
    }
    
    return cutoff
  }

  // Helper function to check if a date is within the filter range
  const isDateInRange = (dateString) => {
    if (!dateString) return true
    
    // Check period filter
    const periodCutoff = getPeriodCutoff(selectedPeriod)
    if (periodCutoff) {
      const date = new Date(dateString)
      if (date < periodCutoff) return false
    }
    
    return true
  }

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedPeriod('all')
    setOrgStatusFilter('')
    setProductStatusFilter('')
  }

  // Fetch all organizations with user counts - optimized caching
  const { data: organizationsData, isLoading: orgsLoading } = useQuery(
    'reports-organizations',
    () => organizationsAPI.getAll({ limit: 1000, status: 'active' }).then(res => res.data),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  // Fetch organization statistics - optimized caching
  const { data: orgStatsData, isLoading: statsLoading } = useQuery(
    'reports-org-stats',
    () => organizationsAPI.getStats().then(res => res.data),
    {
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  // Fetch all organizations for status distribution - optimized caching
  const { data: allOrgsData, isLoading: allOrgsLoading } = useQuery(
    'reports-all-organizations',
    () => organizationsAPI.getAll({ limit: 1000 }).then(res => res.data),
    {
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  // Fetch subscriptions for all organizations - optimized with caching
  const organizations = allOrgsData?.data?.organizations || []
  const subscriptionsQueries = useQueries(
    organizations.map(org => ({
      queryKey: ['subscriptions', org.id, 'reports'],
      queryFn: () => api.get(`/subscriptions/organization/${org.id}`).then(r => r.data),
      enabled: organizations.length > 0 && !allOrgsLoading,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onError: (error) => {
        // Silently handle errors for organizations without subscriptions
        if (error.response?.status !== 404) {
          console.warn(`Failed to fetch subscriptions for organization ${org.id}:`, error)
        }
      }
    }))
  )

  // Fetch payments for all organizations - optimized with caching
  const paymentsQueries = useQueries(
    organizations.map(org => ({
      queryKey: ['payments', org.id, 'reports'],
      queryFn: () => paymentsAPI.getByOrganization(org.id).then(r => r.data),
      enabled: organizations.length > 0 && !allOrgsLoading,
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onError: (error) => {
        // Silently handle errors for organizations without payments
        if (error.response?.status !== 404) {
          console.warn(`Failed to fetch payments for organization ${org.id}:`, error)
        }
      }
    }))
  )

  // Fetch all products - optimized caching
  const { data: productsData, isLoading: productsLoading } = useQuery(
    'reports-products',
    () => productsAPI.getAll({ limit: 1000 }).then(res => res.data),
    {
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  // Fetch product details (roles, modules, features) for all products
  const products = productsData?.data?.products || []
  const productIds = products.map(p => p.id).join(',')
  
  // Fetch roles for each product (we'll do this in parallel) - optimized caching
  const { data: productsWithDetails, isLoading: productsDetailsLoading } = useQuery(
    ['reports-products-details', productIds],
    async () => {
      if (products.length === 0) return []
      
      // Fetch details for all products in parallel
      const detailsPromises = products.map(async (product) => {
        try {
          // Fetch product details which includes modules and features
          const detailsRes = await productsAPI.getDetails(product.id)
          const details = detailsRes.data?.data?.product || {}
          
          // Fetch roles separately
          let roles = []
          try {
            const rolesRes = await productsAPI.getRoles(product.id)
            roles = rolesRes.data?.data?.roles || rolesRes.data?.roles || []
          } catch (error) {
            console.warn(`Failed to fetch roles for product ${product.id}:`, error)
          }
          
          return {
            ...product,
            modules: details.modules || [],
            features: details.features || [],
            roles: roles,
            module_count: details.modules?.length || product.module_count || 0,
            feature_count: details.features?.length || product.feature_count || 0,
            role_count: roles.length || 0
          }
        } catch (error) {
          console.warn(`Failed to fetch details for product ${product.id}:`, error)
          return {
            ...product,
            modules: [],
            features: [],
            roles: [],
            module_count: product.module_count || 0,
            feature_count: product.feature_count || 0,
            role_count: 0
          }
        }
      })
      
      return Promise.all(detailsPromises)
    },
    {
      enabled: products.length > 0 && !productsLoading,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  // Prepare chart data for organization users
  const chartData = organizationsData?.data?.organizations
    ?.map(org => ({
      name: org.name.length > 20 ? org.name.substring(0, 20) + '...' : org.name,
      fullName: org.name,
      users: org.user_count || 0
    }))
    .sort((a, b) => b.users - a.users) || []

  // Prepare pie chart data for organization users (only organizations with users)
  const orgUsersPieData = chartData
    .filter(org => org.users > 0)
    .map((org, index) => ({
      name: org.name,
      fullName: org.fullName,
      value: org.users,
      color: `hsl(${(index * 137.5) % 360}, 70%, 50%)`
    }))

  // Prepare organization statistics bar chart data
  const orgStatsChartData = orgStatsData?.data?.stats ? [
    {
      name: 'Total',
      value: orgStatsData.data.stats.total_organizations || 0,
      color: '#3B82F6'
    },
    {
      name: 'Active',
      value: orgStatsData.data.stats.active_organizations || 0,
      color: '#10B981'
    },
    {
      name: 'Inactive',
      value: orgStatsData.data.stats.inactive_organizations || 0,
      color: '#F59E0B'
    }
  ] : []

  // Prepare status distribution pie chart data
  const statusDistribution = allOrgsData?.data?.organizations?.reduce((acc, org) => {
    const status = org.status || 'unknown'
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {}) || {}

  const pieChartData = Object.entries(statusDistribution).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count,
    color: status === 'active' ? '#10B981' : 
           status === 'inactive' ? '#F59E0B' : 
           status === 'archived' ? '#EF4444' : 
           status === 'suspended' ? '#8B5CF6' : '#6B7280'
  }))

  // Prepare product charts data
  const productsWithData = productsWithDetails || products
  
  // Products with modules, features, and roles count
  const productsModulesFeaturesRolesData = productsWithData
    .map(product => ({
      name: product.name?.length > 15 ? product.name.substring(0, 15) + '...' : product.name || 'Unknown',
      fullName: product.name || 'Unknown',
      modules: parseInt(product.module_count) || 0,
      features: parseInt(product.feature_count) || 0,
      roles: parseInt(product.role_count) || 0
    }))
    .sort((a, b) => (b.modules + b.features + b.roles) - (a.modules + a.features + a.roles))
    .slice(0, 10) // Top 10 products

  // Total counts across all products
  const totalModules = productsWithData.reduce((sum, p) => sum + (parseInt(p.module_count) || 0), 0)
  const totalFeatures = productsWithData.reduce((sum, p) => sum + (parseInt(p.feature_count) || 0), 0)
  const totalRoles = productsWithData.reduce((sum, p) => sum + (parseInt(p.role_count) || 0), 0)

  // Prepare organization subscriptions data
  const orgSubscriptionsData = organizations.map((org, index) => {
    const subscriptionsData = subscriptionsQueries[index]?.data
    const subscriptions = subscriptionsData?.data?.subscriptions || []
    return {
      organization: org,
      subscriptions: subscriptions
    }
  }).filter(item => item.subscriptions.length > 0) // Only include organizations with subscriptions

  // Fetch users for organizations with subscriptions (for charts)
  const usersQueries = useQueries(
    orgSubscriptionsData.map(item => ({
      queryKey: ['users', item.organization.id, 'reports'],
      queryFn: () => api.get(`/users/organization/${item.organization.id}`).then(r => r.data),
      enabled: orgSubscriptionsData.length > 0 && !allOrgsLoading,
      refetchOnWindowFocus: false,
      onError: (error) => {
        if (error.response?.status !== 404) {
          console.warn(`Failed to fetch users for organization ${item.organization.id}:`, error)
        }
      }
    }))
  )

  // Fetch users for all organizations (for user details table)
  const allUsersQueries = useQueries(
    organizations.map(org => ({
      queryKey: ['users', org.id, 'all-reports'],
      queryFn: () => api.get(`/users/organization/${org.id}`).then(r => r.data),
      enabled: organizations.length > 0 && !allOrgsLoading,
      refetchOnWindowFocus: false,
      onError: (error) => {
        if (error.response?.status !== 404) {
          console.warn(`Failed to fetch users for organization ${org.id}:`, error)
        }
      }
    }))
  )

  // Prepare chart data for organizations with subscribed products and plans
  const orgSubscriptionsChartData = orgSubscriptionsData
    .map(item => {
      const org = item.organization
      const subs = item.subscriptions
      
      // Group subscriptions by product and plan
      const productPlanGroups = {}
      subs.forEach(sub => {
        const productName = sub.product_name || 'Unknown Product'
        const planName = sub.plan_name || 'No Plan'
        const key = `${productName} - ${planName}`
        
        if (!productPlanGroups[key]) {
          productPlanGroups[key] = {
            product: productName,
            plan: planName,
            count: 0
          }
        }
        productPlanGroups[key].count++
      })
      
      return {
        name: org.name?.length > 20 ? org.name.substring(0, 20) + '...' : org.name || 'Unknown',
        fullName: org.name || 'Unknown',
        subscriptions: subs.length,
        productPlans: Object.values(productPlanGroups)
      }
    })
    .sort((a, b) => b.subscriptions - a.subscriptions)
    .slice(0, 15) // Top 15 organizations

  // Flatten data for detailed view
  const orgSubscriptionsFlatData = orgSubscriptionsChartData.flatMap(org => {
    if (org.productPlans.length === 0) return []
    return org.productPlans.map(pp => ({
      orgName: org.name,
      orgFullName: org.fullName,
      productPlan: `${pp.product} - ${pp.plan}`,
      product: pp.product,
      plan: pp.plan,
      count: pp.count
    }))
  })

  // Prepare product subscription pie chart data
  // Count unique organizations per product
  const productOrgCount = {}
  orgSubscriptionsData.forEach(item => {
    const orgId = item.organization.id
    item.subscriptions.forEach(sub => {
      const productName = sub.product_name || 'Unknown Product'
      if (!productOrgCount[productName]) {
        productOrgCount[productName] = new Set()
      }
      productOrgCount[productName].add(orgId)
    })
  })

  // Convert to array format for pie chart
  const productSubscriptionPieData = Object.entries(productOrgCount)
    .map(([productName, orgSet]) => ({
      name: productName.length > 25 ? productName.substring(0, 25) + '...' : productName,
      fullName: productName,
      value: orgSet.size,
      color: `hsl(${(Object.keys(productOrgCount).indexOf(productName) * 137.5) % 360}, 70%, 50%)`
    }))
    .sort((a, b) => b.value - a.value)

  // Prepare product user count data
  // Map users to products via their subscription_id
  const productUserCount = {}
  orgSubscriptionsData.forEach((item, orgIndex) => {
    const usersData = usersQueries[orgIndex]?.data
    const users = usersData?.data?.users || usersData?.data?.data?.users || []
    
    // Create a map of subscription_id to product_id
    const subscriptionToProduct = {}
    item.subscriptions.forEach(sub => {
      subscriptionToProduct[sub.id] = {
        product_id: sub.product_id,
        product_name: sub.product_name || 'Unknown Product'
      }
    })
    
    // Count users per product
    users.forEach(user => {
      // Check subscription_id (single)
      if (user.subscription_id && subscriptionToProduct[user.subscription_id]) {
        const productName = subscriptionToProduct[user.subscription_id].product_name
        if (!productUserCount[productName]) {
          productUserCount[productName] = new Set()
        }
        productUserCount[productName].add(user.id)
      }
      
      // Check subscription_ids (array)
      if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
        user.subscription_ids.forEach(subId => {
          if (subscriptionToProduct[subId]) {
            const productName = subscriptionToProduct[subId].product_name
            if (!productUserCount[productName]) {
              productUserCount[productName] = new Set()
            }
            productUserCount[productName].add(user.id)
          }
        })
      }
    })
  })

  // Convert to array format for bar chart
  const productUserCountChartData = Object.entries(productUserCount)
    .map(([productName, userSet]) => ({
      name: productName.length > 20 ? productName.substring(0, 20) + '...' : productName,
      fullName: productName,
      users: userSet.size
    }))
    .sort((a, b) => b.users - a.users)

  const subscriptionsLoading = subscriptionsQueries.some(q => q.isLoading)
  const usersLoading = usersQueries.some(q => q.isLoading)
  const isLoading = orgsLoading || statsLoading || allOrgsLoading || productsLoading || productsDetailsLoading || subscriptionsLoading || usersLoading

  // Filter data based on date range and status
  const filteredOrganizations = allOrgsData?.data?.organizations?.filter(org => {
    const dateMatch = isDateInRange(org.created_at)
    const statusMatch = !orgStatusFilter || org.status === orgStatusFilter
    return dateMatch && statusMatch
  }) || []
  
  const filteredProducts = productsWithDetails?.filter(product => {
    const dateMatch = isDateInRange(product.created_at)
    const statusMatch = !productStatusFilter || product.status === productStatusFilter
    return dateMatch && statusMatch
  }) || []

  // Filter chart data based on filtered organizations and products
  const filteredChartData = chartData?.filter(org => {
    const orgData = filteredOrganizations?.find(o => o.name === org.fullName)
    return orgData !== undefined
  }) || []
  
  const filteredOrgUsersPieData = orgUsersPieData?.filter(org => {
    const orgData = filteredOrganizations?.find(o => o.name === org.fullName || o.name === org.name)
    return orgData !== undefined
  }) || []
  
  const filteredProductSubscriptionPieData = productSubscriptionPieData?.filter(item => {
    const product = filteredProducts?.find(p => p.name === item.fullName || p.name === item.name)
    return product !== undefined
  }) || []
  
  const filteredProductUserCountData = productUserCountChartData?.filter(item => {
    const product = filteredProducts?.find(p => p.name === item.fullName || p.name === item.name)
    return product !== undefined
  }) || []
  
  const filteredModulesFeaturesRolesData = productsModulesFeaturesRolesData?.filter(item => {
    const product = filteredProducts?.find(p => p.name === item.fullName || p.name === item.name)
    return product !== undefined
  }) || []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600 mt-1">View and generate reports</p>
      </div>

      {/* Filters Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters</span>
            </div>
            <div className="flex flex-col gap-4">
              {/* First Row: Period, Organization Status, Product Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Period Filter */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">Period</label>
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Time</option>
                    <option value="monthly">Last Month</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="1year">Last Year</option>
                  </select>
                </div>

                {/* Organization Status Filter */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">Organization Status</label>
                  <select
                    value={orgStatusFilter}
                    onChange={(e) => setOrgStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                {/* Product Status Filter */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">Product Status</label>
                  <select
                    value={productStatusFilter}
                    onChange={(e) => setProductStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {(selectedPeriod !== 'all' || orgStatusFilter || productStatusFilter) && (
              <div className="flex justify-end mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Organization Details Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Organization Details
            </CardTitle>
            {filteredOrganizations && filteredOrganizations.length > 0 && (
              <ExportDropdown
                onExportCSV={() => {
                  try {
                    const orgData = filteredOrganizations.map((org) => {
                      const originalIndex = allOrgsData?.data?.organizations?.findIndex(o => o.id === org.id) ?? -1
                      const orgWithUsers = organizationsData?.data?.organizations?.find(o => o.id === org.id)
                      const userCount = orgWithUsers?.user_count || 0
                      const subscriptionsData = subscriptionsQueries[originalIndex]?.data
                      const subscriptions = subscriptionsData?.data?.subscriptions || []
                      const subscriptionCount = subscriptions.length
                      const paymentsData = paymentsQueries[originalIndex]?.data
                      const payments = paymentsData?.data?.payments || []
                      const paidAmount = payments.filter(p => p.payment_status === 'completed').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                      return {
                        Organization: org.name,
                        Status: org.status || 'active',
                        Users: userCount,
                        Subscriptions: subscriptionCount,
                        'Paid Amount': paidAmount > 0 ? `$${paidAmount.toFixed(2)}` : '$0.00',
                        'Created Date': org.created_at ? new Date(org.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                      }
                    })
                    const columns = [
                      { key: 'Organization', label: 'Organization' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Users', label: 'Users' },
                      { key: 'Subscriptions', label: 'Subscriptions' },
                      { key: 'Paid Amount', label: 'Paid Amount' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToCSV(orgData, columns, 'organization-details')
                    toast.success('Organization details exported to CSV')
                  } catch (error) {
                    toast.error('Failed to export organization details')
                    console.error(error)
                  }
                }}
                onExportExcel={() => {
                  try {
                    const orgData = filteredOrganizations.map((org) => {
                      const originalIndex = allOrgsData?.data?.organizations?.findIndex(o => o.id === org.id) ?? -1
                      const orgWithUsers = organizationsData?.data?.organizations?.find(o => o.id === org.id)
                      const userCount = orgWithUsers?.user_count || 0
                      const subscriptionsData = subscriptionsQueries[originalIndex]?.data
                      const subscriptions = subscriptionsData?.data?.subscriptions || []
                      const subscriptionCount = subscriptions.length
                      const paymentsData = paymentsQueries[originalIndex]?.data
                      const payments = paymentsData?.data?.payments || []
                      const paidAmount = payments.filter(p => p.payment_status === 'completed').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                      return {
                        Organization: org.name,
                        Status: org.status || 'active',
                        Users: userCount,
                        Subscriptions: subscriptionCount,
                        'Paid Amount': paidAmount > 0 ? `$${paidAmount.toFixed(2)}` : '$0.00',
                        'Created Date': org.created_at ? new Date(org.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                      }
                    })
                    const columns = [
                      { key: 'Organization', label: 'Organization' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Users', label: 'Users' },
                      { key: 'Subscriptions', label: 'Subscriptions' },
                      { key: 'Paid Amount', label: 'Paid Amount' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToExcel(orgData, columns, 'organization-details')
                    toast.success('Organization details exported to Excel')
                  } catch (error) {
                    toast.error('Failed to export organization details')
                    console.error(error)
                  }
                }}
                onExportPDF={() => {
                  try {
                    const orgData = filteredOrganizations.map((org) => {
                      const originalIndex = allOrgsData?.data?.organizations?.findIndex(o => o.id === org.id) ?? -1
                      const orgWithUsers = organizationsData?.data?.organizations?.find(o => o.id === org.id)
                      const userCount = orgWithUsers?.user_count || 0
                      const subscriptionsData = subscriptionsQueries[originalIndex]?.data
                      const subscriptions = subscriptionsData?.data?.subscriptions || []
                      const subscriptionCount = subscriptions.length
                      const paymentsData = paymentsQueries[originalIndex]?.data
                      const payments = paymentsData?.data?.payments || []
                      const paidAmount = payments.filter(p => p.payment_status === 'completed').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                      return {
                        Organization: org.name,
                        Status: org.status || 'active',
                        Users: userCount,
                        Subscriptions: subscriptionCount,
                        'Paid Amount': paidAmount > 0 ? `$${paidAmount.toFixed(2)}` : '$0.00',
                        'Created Date': org.created_at ? new Date(org.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                      }
                    })
                    const columns = [
                      { key: 'Organization', label: 'Organization' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Users', label: 'Users' },
                      { key: 'Subscriptions', label: 'Subscriptions' },
                      { key: 'Paid Amount', label: 'Paid Amount' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToPDF(orgData, columns, 'Organization Details', 'organization-details')
                    toast.success('Organization details exported to PDF')
                  } catch (error) {
                    toast.error('Failed to export organization details')
                    console.error(error)
                  }
                }}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading || orgsLoading || allOrgsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : !filteredOrganizations || filteredOrganizations.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Organizations Found</h3>
              <p className="text-gray-600">No organizations available to display{(selectedPeriod !== 'all' || orgStatusFilter) ? ' for the selected filters' : ''}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscriptions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrganizations.map((org) => {
                    const originalIndex = allOrgsData?.data?.organizations?.findIndex(o => o.id === org.id) ?? -1
                    // Find user count for this organization
                    const orgWithUsers = organizationsData?.data?.organizations?.find(
                      o => o.id === org.id
                    )
                    const userCount = orgWithUsers?.user_count || 0
                    
                    // Get subscription count
                    const subscriptionsData = subscriptionsQueries[originalIndex]?.data
                    const subscriptions = subscriptionsData?.data?.subscriptions || []
                    const subscriptionCount = subscriptions.length
                    
                    // Get paid amount (only completed payments)
                    const paymentsData = paymentsQueries[originalIndex]?.data
                    const payments = paymentsData?.data?.payments || []
                    const paidAmount = payments
                      .filter(p => p.payment_status === 'completed')
                      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                    
                    // Format created date
                    const formatDate = (dateString) => {
                      if (!dateString) return 'N/A'
                      try {
                        const date = new Date(dateString)
                        return date.toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })
                      } catch {
                        return 'N/A'
                      }
                    }
                    
                    // Get status badge color
                    const getStatusColor = (status) => {
                      switch (status?.toLowerCase()) {
                        case 'active':
                          return 'bg-green-100 text-green-800'
                        case 'inactive':
                          return 'bg-yellow-100 text-yellow-800'
                        case 'suspended':
                          return 'bg-red-100 text-red-800'
                        case 'archived':
                          return 'bg-gray-100 text-gray-800'
                        default:
                          return 'bg-gray-100 text-gray-800'
                      }
                    }

                    return (
                      <tr key={org.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {org.logo_url ? (
                              <img
                                src={org.logo_url}
                                alt={org.name}
                                className="h-10 w-10 rounded-full object-cover mr-3"
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                }}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                                <Building2 className="h-5 w-5 text-primary-600" />
                              </div>
                            )}
                            <div className="text-sm font-medium text-gray-900">{org.name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(org.status)}`}>
                            {org.status || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Users className="h-4 w-4 mr-1 text-gray-400" />
                            {userCount.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Package className="h-4 w-4 mr-1 text-gray-400" />
                            {subscriptionCount.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <DollarSign className="h-4 w-4 mr-1 text-green-600" />
                            {paidAmount > 0 ? `$${paidAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                            {formatDate(org.created_at)}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium">
                  Total Organizations: {filteredOrganizations.length} | 
                  Active: {filteredOrganizations.filter(o => o.status === 'active').length} | 
                  Total Users: {organizationsData?.data?.organizations?.reduce((sum, org) => sum + (org.user_count || 0), 0).toLocaleString() || 0} | 
                  Total Subscriptions: {subscriptionsQueries.reduce((sum, query) => {
                    const subscriptions = query?.data?.data?.subscriptions || []
                    return sum + subscriptions.length
                  }, 0).toLocaleString()} | 
                  Total Paid: ${paymentsQueries.reduce((sum, query) => {
                    const payments = query?.data?.data?.payments || []
                    const orgPaid = payments
                      .filter(p => p.payment_status === 'completed')
                      .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
                    return sum + orgPaid
                  }, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Details Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Product Details
            </CardTitle>
            {filteredProducts && filteredProducts.length > 0 && (
              <ExportDropdown
                onExportCSV={() => {
                  try {
                    const productData = filteredProducts.map((product) => {
                      const productSubscriptionCount = subscriptionsQueries.reduce((count, query) => {
                        const subscriptions = query?.data?.data?.subscriptions || []
                        return count + subscriptions.filter(sub => sub.product_id === product.id).length
                      }, 0)
                      const productUserCount = (() => {
                        const userSet = new Set()
                        orgSubscriptionsData.forEach((item, orgIndex) => {
                          const usersData = usersQueries[orgIndex]?.data
                          const users = usersData?.data?.users || usersData?.data?.data?.users || []
                          const productSubscriptionIds = item.subscriptions.filter(sub => sub.product_id === product.id).map(sub => sub.id)
                          users.forEach(user => {
                            if (user.subscription_id && productSubscriptionIds.includes(user.subscription_id)) {
                              userSet.add(user.id)
                            }
                            if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                              user.subscription_ids.forEach(subId => {
                                if (productSubscriptionIds.includes(subId)) userSet.add(user.id)
                              })
                            }
                          })
                        })
                        return userSet.size
                      })()
                      return {
                        Product: product.name,
                        Status: product.status || 'active',
                        Modules: product.module_count || 0,
                        Features: product.feature_count || 0,
                        Roles: product.role_count || 0,
                        Subscriptions: productSubscriptionCount,
                        Users: productUserCount,
                        'Created Date': product.created_at ? new Date(product.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                      }
                    })
                    const columns = [
                      { key: 'Product', label: 'Product' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Modules', label: 'Modules' },
                      { key: 'Features', label: 'Features' },
                      { key: 'Roles', label: 'Roles' },
                      { key: 'Subscriptions', label: 'Subscriptions' },
                      { key: 'Users', label: 'Users' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToCSV(productData, columns, 'product-details')
                    toast.success('Product details exported to CSV')
                  } catch (error) {
                    toast.error('Failed to export product details')
                    console.error(error)
                  }
                }}
                onExportExcel={() => {
                  try {
                    const productData = filteredProducts.map((product) => {
                      const productSubscriptionCount = subscriptionsQueries.reduce((count, query) => {
                        const subscriptions = query?.data?.data?.subscriptions || []
                        return count + subscriptions.filter(sub => sub.product_id === product.id).length
                      }, 0)
                      const productUserCount = (() => {
                        const userSet = new Set()
                        orgSubscriptionsData.forEach((item, orgIndex) => {
                          const usersData = usersQueries[orgIndex]?.data
                          const users = usersData?.data?.users || usersData?.data?.data?.users || []
                          const productSubscriptionIds = item.subscriptions.filter(sub => sub.product_id === product.id).map(sub => sub.id)
                          users.forEach(user => {
                            if (user.subscription_id && productSubscriptionIds.includes(user.subscription_id)) {
                              userSet.add(user.id)
                            }
                            if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                              user.subscription_ids.forEach(subId => {
                                if (productSubscriptionIds.includes(subId)) userSet.add(user.id)
                              })
                            }
                          })
                        })
                        return userSet.size
                      })()
                      return {
                        Product: product.name,
                        Status: product.status || 'active',
                        Modules: product.module_count || 0,
                        Features: product.feature_count || 0,
                        Roles: product.role_count || 0,
                        Subscriptions: productSubscriptionCount,
                        Users: productUserCount,
                        'Created Date': product.created_at ? new Date(product.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                      }
                    })
                    const columns = [
                      { key: 'Product', label: 'Product' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Modules', label: 'Modules' },
                      { key: 'Features', label: 'Features' },
                      { key: 'Roles', label: 'Roles' },
                      { key: 'Subscriptions', label: 'Subscriptions' },
                      { key: 'Users', label: 'Users' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToExcel(productData, columns, 'product-details')
                    toast.success('Product details exported to Excel')
                  } catch (error) {
                    toast.error('Failed to export product details')
                    console.error(error)
                  }
                }}
                onExportPDF={() => {
                  try {
                    const productData = filteredProducts.map((product) => {
                      const productSubscriptionCount = subscriptionsQueries.reduce((count, query) => {
                        const subscriptions = query?.data?.data?.subscriptions || []
                        return count + subscriptions.filter(sub => sub.product_id === product.id).length
                      }, 0)
                      const productUserCount = (() => {
                        const userSet = new Set()
                        orgSubscriptionsData.forEach((item, orgIndex) => {
                          const usersData = usersQueries[orgIndex]?.data
                          const users = usersData?.data?.users || usersData?.data?.data?.users || []
                          const productSubscriptionIds = item.subscriptions.filter(sub => sub.product_id === product.id).map(sub => sub.id)
                          users.forEach(user => {
                            if (user.subscription_id && productSubscriptionIds.includes(user.subscription_id)) {
                              userSet.add(user.id)
                            }
                            if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                              user.subscription_ids.forEach(subId => {
                                if (productSubscriptionIds.includes(subId)) userSet.add(user.id)
                              })
                            }
                          })
                        })
                        return userSet.size
                      })()
                      return {
                        Product: product.name,
                        Status: product.status || 'active',
                        Modules: product.module_count || 0,
                        Features: product.feature_count || 0,
                        Roles: product.role_count || 0,
                        Subscriptions: productSubscriptionCount,
                        Users: productUserCount,
                        'Created Date': product.created_at ? new Date(product.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                      }
                    })
                    const columns = [
                      { key: 'Product', label: 'Product' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Modules', label: 'Modules' },
                      { key: 'Features', label: 'Features' },
                      { key: 'Roles', label: 'Roles' },
                      { key: 'Subscriptions', label: 'Subscriptions' },
                      { key: 'Users', label: 'Users' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToPDF(productData, columns, 'Product Details', 'product-details')
                    toast.success('Product details exported to PDF')
                  } catch (error) {
                    toast.error('Failed to export product details')
                    console.error(error)
                  }
                }}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading || productsLoading || productsDetailsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : !filteredProducts || filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
              <p className="text-gray-600">No products available to display{(selectedPeriod !== 'all' || productStatusFilter) ? ' for the selected filters' : ''}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modules
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Features
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Roles
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscriptions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map((product) => {
                    // Count subscriptions for this product
                    const productSubscriptionCount = subscriptionsQueries.reduce((count, query) => {
                      const subscriptions = query?.data?.data?.subscriptions || []
                      const productSubs = subscriptions.filter(sub => sub.product_id === product.id)
                      return count + productSubs.length
                    }, 0)
                    
                    // Count users subscribed to this product
                    const productUserCount = (() => {
                      const userSet = new Set()
                      orgSubscriptionsData.forEach((item, orgIndex) => {
                        const usersData = usersQueries[orgIndex]?.data
                        const users = usersData?.data?.users || usersData?.data?.data?.users || []
                        
                        // Get subscription IDs for this product
                        const productSubscriptionIds = item.subscriptions
                          .filter(sub => sub.product_id === product.id)
                          .map(sub => sub.id)
                        
                        // Count users with these subscription IDs
                        users.forEach(user => {
                          if (user.subscription_id && productSubscriptionIds.includes(user.subscription_id)) {
                            userSet.add(user.id)
                          }
                          if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                            user.subscription_ids.forEach(subId => {
                              if (productSubscriptionIds.includes(subId)) {
                                userSet.add(user.id)
                              }
                            })
                          }
                        })
                      })
                      return userSet.size
                    })()
                    
                    // Format created date
                    const formatDate = (dateString) => {
                      if (!dateString) return 'N/A'
                      try {
                        const date = new Date(dateString)
                        return date.toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })
                      } catch {
                        return 'N/A'
                      }
                    }
                    
                    // Get status badge color
                    const getStatusColor = (status) => {
                      switch (status?.toLowerCase()) {
                        case 'active':
                          return 'bg-green-100 text-green-800'
                        case 'inactive':
                          return 'bg-yellow-100 text-yellow-800'
                        case 'draft':
                          return 'bg-gray-100 text-gray-800'
                        default:
                          return 'bg-gray-100 text-gray-800'
                      }
                    }

                    return (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                              <Package className="h-5 w-5 text-primary-600" />
                            </div>
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(product.status)}`}>
                            {product.status || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Layers className="h-4 w-4 mr-1 text-gray-400" />
                            {(product.module_count || 0).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Zap className="h-4 w-4 mr-1 text-gray-400" />
                            {(product.feature_count || 0).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Shield className="h-4 w-4 mr-1 text-gray-400" />
                            {(product.role_count || 0).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <ShoppingCart className="h-4 w-4 mr-1 text-gray-400" />
                            {productSubscriptionCount.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Users className="h-4 w-4 mr-1 text-gray-400" />
                            {productUserCount.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                            {formatDate(product.created_at)}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium">
                  Total Products: {filteredProducts.length} | 
                  Active: {productsWithDetails.filter(p => p.status === 'active').length} | 
                  Total Modules: {productsWithDetails.reduce((sum, p) => sum + (p.module_count || 0), 0).toLocaleString()} | 
                  Total Features: {productsWithDetails.reduce((sum, p) => sum + (p.feature_count || 0), 0).toLocaleString()} | 
                  Total Roles: {productsWithDetails.reduce((sum, p) => sum + (p.role_count || 0), 0).toLocaleString()} | 
                  Total Subscriptions: {subscriptionsQueries.reduce((sum, query) => {
                    const subscriptions = query?.data?.data?.subscriptions || []
                    return sum + subscriptions.length
                  }, 0).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Organization User Details Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Organization User Details
            </CardTitle>
            {organizations && organizations.length > 0 && (
              <ExportDropdown
                onExportCSV={() => {
                  try {
                    const userData = []
                    organizations.forEach((org, orgIndex) => {
                      const usersData = allUsersQueries[orgIndex]?.data
                      const users = usersData?.data?.users || usersData?.data?.data?.users || []
                      const orgSubscriptionsData = subscriptionsQueries[orgIndex]?.data
                      const orgSubscriptions = orgSubscriptionsData?.data?.subscriptions || []
                      const subscriptionToProduct = {}
                      orgSubscriptions.forEach(sub => {
                        subscriptionToProduct[sub.id] = sub.product_name || 'Unknown Product'
                      })
                      users.forEach((user) => {
                        const userProducts = new Set()
                        if (user.subscription_id && subscriptionToProduct[user.subscription_id]) {
                          userProducts.add(subscriptionToProduct[user.subscription_id])
                        }
                        if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                          user.subscription_ids.forEach(subId => {
                            if (subscriptionToProduct[subId]) {
                              userProducts.add(subscriptionToProduct[subId])
                            }
                          })
                        }
                        userData.push({
                          User: user.full_name || user.username || 'N/A',
                          Email: user.email,
                          Organization: org.name,
                          Status: user.status || 'active',
                          Products: userProducts.size,
                          'Created Date': user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                        })
                      })
                    })
                    const columns = [
                      { key: 'User', label: 'User' },
                      { key: 'Email', label: 'Email' },
                      { key: 'Organization', label: 'Organization' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Products', label: 'Products' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToCSV(userData, columns, 'organization-user-details')
                    toast.success('User details exported to CSV')
                  } catch (error) {
                    toast.error('Failed to export user details')
                    console.error(error)
                  }
                }}
                onExportExcel={() => {
                  try {
                    const userData = []
                    organizations.forEach((org, orgIndex) => {
                      const usersData = allUsersQueries[orgIndex]?.data
                      const users = usersData?.data?.users || usersData?.data?.data?.users || []
                      const orgSubscriptionsData = subscriptionsQueries[orgIndex]?.data
                      const orgSubscriptions = orgSubscriptionsData?.data?.subscriptions || []
                      const subscriptionToProduct = {}
                      orgSubscriptions.forEach(sub => {
                        subscriptionToProduct[sub.id] = sub.product_name || 'Unknown Product'
                      })
                      users.forEach((user) => {
                        const userProducts = new Set()
                        if (user.subscription_id && subscriptionToProduct[user.subscription_id]) {
                          userProducts.add(subscriptionToProduct[user.subscription_id])
                        }
                        if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                          user.subscription_ids.forEach(subId => {
                            if (subscriptionToProduct[subId]) {
                              userProducts.add(subscriptionToProduct[subId])
                            }
                          })
                        }
                        userData.push({
                          User: user.full_name || user.username || 'N/A',
                          Email: user.email,
                          Organization: org.name,
                          Status: user.status || 'active',
                          Products: userProducts.size,
                          'Created Date': user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                        })
                      })
                    })
                    const columns = [
                      { key: 'User', label: 'User' },
                      { key: 'Email', label: 'Email' },
                      { key: 'Organization', label: 'Organization' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Products', label: 'Products' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToExcel(userData, columns, 'organization-user-details')
                    toast.success('User details exported to Excel')
                  } catch (error) {
                    toast.error('Failed to export user details')
                    console.error(error)
                  }
                }}
                onExportPDF={() => {
                  try {
                    const userData = []
                    organizations.forEach((org, orgIndex) => {
                      const usersData = allUsersQueries[orgIndex]?.data
                      const users = usersData?.data?.users || usersData?.data?.data?.users || []
                      const orgSubscriptionsData = subscriptionsQueries[orgIndex]?.data
                      const orgSubscriptions = orgSubscriptionsData?.data?.subscriptions || []
                      const subscriptionToProduct = {}
                      orgSubscriptions.forEach(sub => {
                        subscriptionToProduct[sub.id] = sub.product_name || 'Unknown Product'
                      })
                      users.forEach((user) => {
                        const userProducts = new Set()
                        if (user.subscription_id && subscriptionToProduct[user.subscription_id]) {
                          userProducts.add(subscriptionToProduct[user.subscription_id])
                        }
                        if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                          user.subscription_ids.forEach(subId => {
                            if (subscriptionToProduct[subId]) {
                              userProducts.add(subscriptionToProduct[subId])
                            }
                          })
                        }
                        userData.push({
                          User: user.full_name || user.username || 'N/A',
                          Email: user.email,
                          Organization: org.name,
                          Status: user.status || 'active',
                          Products: userProducts.size,
                          'Created Date': user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
                        })
                      })
                    })
                    const columns = [
                      { key: 'User', label: 'User' },
                      { key: 'Email', label: 'Email' },
                      { key: 'Organization', label: 'Organization' },
                      { key: 'Status', label: 'Status' },
                      { key: 'Products', label: 'Products' },
                      { key: 'Created Date', label: 'Created Date' }
                    ]
                    exportToPDF(userData, columns, 'Organization User Details', 'organization-user-details')
                    toast.success('User details exported to PDF')
                  } catch (error) {
                    toast.error('Failed to export user details')
                    console.error(error)
                  }
                }}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading || allOrgsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : !organizations || organizations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Organizations Found</h3>
              <p className="text-gray-600">No organizations available to display users.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Products
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizations.map((org, orgIndex) => {
                    const usersData = allUsersQueries[orgIndex]?.data
                    const users = usersData?.data?.users || usersData?.data?.data?.users || []
                    
                    // Get subscriptions for this organization
                    const orgSubscriptionsData = subscriptionsQueries[orgIndex]?.data
                    const orgSubscriptions = orgSubscriptionsData?.data?.subscriptions || []
                    
                    // Create a map of subscription_id to product_name
                    const subscriptionToProduct = {}
                    orgSubscriptions.forEach(sub => {
                      subscriptionToProduct[sub.id] = sub.product_name || 'Unknown Product'
                    })
                    
                    return users.map((user) => {
                      // Get products for this user
                      const userProducts = new Set()
                      if (user.subscription_id && subscriptionToProduct[user.subscription_id]) {
                        userProducts.add(subscriptionToProduct[user.subscription_id])
                      }
                      if (user.subscription_ids && Array.isArray(user.subscription_ids)) {
                        user.subscription_ids.forEach(subId => {
                          if (subscriptionToProduct[subId]) {
                            userProducts.add(subscriptionToProduct[subId])
                          }
                        })
                      }
                      
                      // Format created date
                      const formatDate = (dateString) => {
                        if (!dateString) return 'N/A'
                        try {
                          const date = new Date(dateString)
                          return date.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })
                        } catch {
                          return 'N/A'
                        }
                      }
                      
                      // Get status badge color
                      const getStatusColor = (status) => {
                        switch (status?.toLowerCase()) {
                          case 'active':
                            return 'bg-green-100 text-green-800'
                          case 'inactive':
                            return 'bg-yellow-100 text-yellow-800'
                          case 'suspended':
                            return 'bg-red-100 text-red-800'
                          case 'archived':
                            return 'bg-gray-100 text-gray-800'
                          default:
                            return 'bg-gray-100 text-gray-800'
                        }
                      }
                      
                      return (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                                <Users className="h-5 w-5 text-primary-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {user.full_name || user.username || 'N/A'}
                                </div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 flex items-center">
                              <Building2 className="h-4 w-4 mr-1 text-gray-400" />
                              {org.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.status)}`}>
                              {user.status || 'active'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {userProducts.size > 0 ? (
                                userProducts.size.toLocaleString()
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 flex items-center">
                              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                              {formatDate(user.created_at)}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  })}
                </tbody>
              </table>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium">
                  Total Users: {allUsersQueries.reduce((sum, query) => {
                    const users = query?.data?.data?.users || query?.data?.data?.data?.users || []
                    return sum + users.length
                  }, 0).toLocaleString()} | 
                  Active: {allUsersQueries.reduce((sum, query) => {
                    const users = query?.data?.data?.users || query?.data?.data?.data?.users || []
                    return sum + users.filter(u => u.status === 'active').length
                  }, 0).toLocaleString()} | 
                  Organizations: {organizations.length}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Organization Statistics and Organization Users - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Organization Statistics Graph */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="h-5 w-5 mr-2" />
            Organization Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : orgStatsChartData.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600">No organization statistics found.</p>
            </div>
          ) : (
            <div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={orgStatsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [value, 'Organizations']}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar 
                    dataKey="value" 
                    name="Organizations"
                    radius={[8, 8, 0, 0]}
                  >
                    {orgStatsChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium">
                  Average Employee Count: {orgStatsData?.data?.stats?.avg_employee_count 
                  ? Math.round(orgStatsData.data.stats.avg_employee_count).toLocaleString() 
                    : 'N/A'}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Organization Users Graph */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Organization Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredOrgUsersPieData.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600">No organizations with users found to display.</p>
            </div>
          ) : (
            <div>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={orgUsersPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {filteredOrgUsersPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => {
                      const fullName = props.payload.fullName
                      return [
                        `${value} user${value !== 1 ? 's' : ''}`,
                        fullName || name
                      ]
                    }}
                    labelFormatter={(label) => {
                      const entry = filteredOrgUsersPieData.find(item => item.name === label)
                      return entry?.fullName || label
                    }}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredOrgUsersPieData.slice(0, 10).map((entry, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded flex-shrink-0" 
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    <span className="text-sm text-gray-600 truncate" title={entry.fullName}>
                      {entry.name}: <span className="font-medium">{entry.value}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium">
                  Total Organizations: {filteredChartData.length} | 
                  Total Users: {filteredChartData.reduce((sum, org) => sum + org.users, 0).toLocaleString()}
                </p>
              </div>
          </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Product Details Graphs */}
      {/* Modules, Features & Roles Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Layers className="h-5 w-5 mr-2" />
            Modules, Features & Roles Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : productsWithData.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600">No products found to display.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Modules Summary */}
              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Layers className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-blue-600">{totalModules}</p>
                    <p className="text-sm text-gray-600">Total Modules</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Average: {productsWithData.length > 0 
                    ? (totalModules / productsWithData.length).toFixed(1) 
                    : 0} per product
                </p>
              </div>

              {/* Features Summary */}
              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Zap className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-green-600">{totalFeatures}</p>
                    <p className="text-sm text-gray-600">Total Features</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Average: {productsWithData.length > 0 
                    ? (totalFeatures / productsWithData.length).toFixed(1) 
                    : 0} per product
                </p>
              </div>

              {/* Roles Summary */}
              <div className="bg-purple-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Shield className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-purple-600">{totalRoles}</p>
                    <p className="text-sm text-gray-600">Total Roles</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Average: {productsWithData.length > 0 
                    ? (totalRoles / productsWithData.length).toFixed(1) 
                    : 0} per product
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Products - Modules, Features & Roles Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Package className="h-5 w-5 mr-2" />
            Products - Modules, Features & Roles
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredModulesFeaturesRolesData.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600">No products found to display{(selectedPeriod !== 'all' || productStatusFilter) ? ' for the selected filters' : ''}.</p>
            </div>
          ) : (
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={filteredModulesFeaturesRolesData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      // name parameter contains the dataKey ('modules', 'features', or 'roles')
                      const dataKey = String(name || '').toLowerCase()
                      if (dataKey === 'modules') {
                        return [value, 'Modules']
                      } else if (dataKey === 'features') {
                        return [value, 'Features']
                      } else if (dataKey === 'roles') {
                        return [value, 'Roles']
                      }
                      return [value, name]
                    }}
                    labelFormatter={(label) => {
                      const fullName = filteredModulesFeaturesRolesData.find(item => item.name === label)?.fullName
                      return fullName || label
                    }}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="modules" fill="#3B82F6" name="Modules" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="features" fill="#10B981" name="Features" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="roles" fill="#8B5CF6" name="Roles" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 text-sm text-gray-600 grid grid-cols-3 gap-2">
                <div>
                  <p className="font-medium">Total Products: {productsWithData.length}</p>
                </div>
                <div>
                  <p className="font-medium">Total Modules: {totalModules}</p>
                </div>
                <div>
                  <p className="font-medium">Total Features: {totalFeatures}</p>
                </div>
                <div className="col-span-3">
                  <p className="font-medium">Total Roles: {totalRoles}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Product Subscription Distribution and Product User Count - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Product Subscription Distribution Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Package className="h-5 w-5 mr-2" />
            Product Subscription Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredProductSubscriptionPieData.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600">No product subscriptions found to display.</p>
            </div>
          ) : (
            <div>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={productSubscriptionPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {filteredProductSubscriptionPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => {
                      const fullName = props.payload.fullName
                      return [
                        `${value} organization${value !== 1 ? 's' : ''}`,
                        fullName || name
                      ]
                    }}
                    labelFormatter={(label) => {
                      const entry = filteredProductSubscriptionPieData.find(item => item.name === label)
                      return entry?.fullName || label
                    }}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {productSubscriptionPieData.map((entry, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded flex-shrink-0" 
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    <span className="text-sm text-gray-600 truncate" title={entry.fullName}>
                      {entry.name}: <span className="font-medium">{entry.value}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium">
                  Total Products: {filteredProductSubscriptionPieData.length} | 
                  Total Organizations: {filteredProductSubscriptionPieData.reduce((sum, item) => sum + item.value, 0)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product User Count Graph */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Product User Count
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredProductUserCountData.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600">No users found for products to display.</p>
            </div>
          ) : (
            <div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={filteredProductUserCountData}
                  margin={{ top: 20, right: 30, left: 60, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    label={{ value: 'Number of Users', angle: -90, position: 'left' }}
                  />
                  <Tooltip 
                    formatter={(value) => [value, 'Users']}
                    labelFormatter={(label) => {
                      const fullName = filteredProductUserCountData.find(item => item.name === label)?.fullName
                      return fullName || label
                    }}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar 
                    dataKey="users" 
                    fill="#10B981" 
                    name="Users"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 text-sm text-gray-600">
                <p>Total Products: {filteredProductUserCountData.length}</p>
                <p>Total Users: {filteredProductUserCountData.reduce((sum, item) => sum + item.users, 0).toLocaleString()}</p>
                <p>Average Users per Product: {filteredProductUserCountData.length > 0 
                  ? (filteredProductUserCountData.reduce((sum, item) => sum + item.users, 0) / filteredProductUserCountData.length).toFixed(1)
                  : 0}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>

    </div>
  )
}

